import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ReportFilterBar from "./ReportFilterBar";
import ReportTable from "./ReportTable";
import Pagination from "../Pagination/Pagination";
import { getTaxReport } from "../../api/reports";
import { formatDateIST, formatINR, formatPercent } from "./reportUtils";

const TaxReport = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    from: searchParams.get("from") || "",
    to: searchParams.get("to") || "",
    q: searchParams.get("search") || searchParams.get("q") || "",
    page: Number(searchParams.get("page")) || 1,
    limit: Number(searchParams.get("limit")) || 10,
    sortBy: searchParams.get("sortBy") || "date",
    sortOrder: searchParams.get("sortOrder") || "desc",
    state: searchParams.get("state") || "",
    gstRate: searchParams.get("gstRate") || "",
    type: searchParams.get("type") || "invoice",
  });

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [reportTotals, setReportTotals] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== "" && value !== null && value !== undefined) {
        params.set(key === "q" ? "search" : key, String(value));
      }
    });

    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  useEffect(() => {
    let cancelled = false;

    const fetchTaxReport = async () => {
      setLoading(true);
      setError("");
      try {
        const { q, ...queryFilters } = filters;
        const response = await getTaxReport({
          ...queryFilters,
          search: q,
        });
        if (cancelled) return;

        const reportRows = response?.data?.rows || response?.rows || [];
        setRows(reportRows);
        setReportTotals(response?.data?.totals || response?.totals || {});
        setPagination(
          response?.pagination || {
            page: filters.page,
            limit: filters.limit,
            total: reportRows.length,
            totalPages: 1,
          },
        );
      } catch (fetchError) {
        if (cancelled) return;
        setRows([]);
        setReportTotals({});
        setPagination({ page: 1, limit: filters.limit, total: 0, totalPages: 1 });
        setError(
          fetchError?.response?.data?.message || fetchError.message || "Failed to load tax report.",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTaxReport();
    return () => {
      cancelled = true;
    };
  }, [
    filters.from,
    filters.to,
    filters.q,
    filters.page,
    filters.limit,
    filters.sortBy,
    filters.sortOrder,
    filters.state,
    filters.gstRate,
    filters.type,
  ]);

  const totals = {
    taxableValue: {
      value: reportTotals.taxableValue || 0,
      format: "inr",
    },
    cgst: {
      value: reportTotals.cgst || 0,
      format: "inr",
    },
    sgst: {
      value: reportTotals.sgst || 0,
      format: "inr",
    },
    igst: {
      value: reportTotals.igst || 0,
      format: "inr",
    },
    total: {
      value: reportTotals.total || 0,
      format: "inr",
    },
  };

  const columns = [
    {
      key: "orderId",
      label: "Order ID",
      sortable: true,
      minWidth: 180,
      render: (row) => (
        <div>
          <div>{row.orderId}</div>
        </div>
      ),
    },
    {
      key: "customerState",
      label: "Customer State",
      sortable: true,
    },
    {
      key: "taxableValue",
      label: "Taxable Value",
      sortable: true,
      render: (row) => formatINR(row.taxableValue),
    },
    {
      key: "gstRate",
      label: "GST %",
      sortable: true,
      render: (row) => formatPercent(row.gstRate),
    },
    {
      key: "cgst",
      label: "CGST",
      sortable: true,
      render: (row) => formatINR(row.cgst),
    },
    {
      key: "sgst",
      label: "SGST",
      sortable: true,
      render: (row) => formatINR(row.sgst),
    },
  ];

  return (
    <div className="container-fluid p-4 report-page">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-0">Tax (GST) Report</h5>
          <small className="text-muted">GST report data</small>
        </div>

        <button type="button" className="btn btn-success" onClick={() => {}}>
          Export CSV
        </button>
      </div>
      <div className="card p-3">
        <ReportFilterBar
          filters={filters}
          onChange={setFilters}
          onReset={setFilters}
          searchPlaceholder="Search order, invoice or customer..."
          filterSlot={
            <>
              <div className="col-12 col-md-4 col-lg-2">
                <label htmlFor="gst-state" className="form-label">
                  State
                </label>

                <select
                  id="gst-state"
                  className="form-select"
                  value={filters.state}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      state: e.target.value,
                      page: 1,
                    }))
                  }
                >
                  <option value="">All States</option>
                  <option value="Andhra Pradesh">Andhra Pradesh</option>
                  <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                  <option value="Assam">Assam</option>
                  <option value="Bihar">Bihar</option>
                  <option value="Chhattisgarh">Chhattisgarh</option>
                  <option value="Goa">Goa</option>
                  <option value="Gujarat">Gujarat</option>
                  <option value="Haryana">Haryana</option>
                  <option value="Himachal Pradesh">Himachal Pradesh</option>
                  <option value="Jharkhand">Jharkhand</option>
                  <option value="Karnataka">Karnataka</option>
                  <option value="Kerala">Kerala</option>
                  <option value="Madhya Pradesh">Madhya Pradesh</option>
                  <option value="Maharashtra">Maharashtra</option>
                  <option value="Manipur">Manipur</option>
                  <option value="Meghalaya">Meghalaya</option>
                  <option value="Mizoram">Mizoram</option>
                  <option value="Nagaland">Nagaland</option>
                  <option value="Odisha">Odisha</option>
                  <option value="Punjab">Punjab</option>
                  <option value="Rajasthan">Rajasthan</option>
                  <option value="Sikkim">Sikkim</option>
                  <option value="Tamil Nadu">Tamil Nadu</option>
                  <option value="Telangana">Telangana</option>
                  <option value="Tripura">Tripura</option>
                  <option value="Uttar Pradesh">Uttar Pradesh</option>
                  <option value="Uttarakhand">Uttarakhand</option>
                  <option value="West Bengal">West Bengal</option>
                </select>
              </div>

              <div className="col-12 col-md-4 col-lg-2">
                <label htmlFor="gst-rate" className="form-label">
                  GST Rate
                </label>

                <select
                  id="gst-rate"
                  className="form-select"
                  value={filters.gstRate}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      gstRate: e.target.value,
                      page: 1,
                    }))
                  }
                >
                  <option value="">All Rates</option>
                  <option value="5">5%</option>
                  <option value="12">12%</option>
                  <option value="18">18%</option>
                </select>
              </div>
            </>
          }
        />

        <ReportTable
          columns={columns}
          rows={rows}
          totals={totals}
          loading={loading}
          error={error}
          page={filters.page}
          total={pagination.total}
          totalPages={pagination.totalPages}
          sortBy={filters.sortBy}
          sortOrder={filters.sortOrder}
          onSort={(sortBy, sortOrder) =>
            setFilters((prev) => ({
              ...prev,
              sortBy,
              sortOrder,
              page: 1,
            }))
          }
          onPageChange={(page) =>
            setFilters((prev) => ({
              ...prev,
              page,
            }))
          }
          rowKey="invoiceNo"
        />
      </div>
      <Pagination
        totalItems={pagination.total}
        itemsPerPage={pagination.limit}
        currentPage={pagination.page}
        onPageChange={(page) =>
          setFilters((prev) => ({
            ...prev,
            page,
          }))
        }
      />
    </div>
  );
};

export default TaxReport;
